import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo, useRef, type Dispatch, type SetStateAction } from 'react';
import { type Phase } from '@/app/utils/vision-biology';
import { supabase } from '@/utils/supabase/client';
import { hasSupabaseConfig } from '@/utils/supabase/info';

export interface ClientScope {
  id: string;
  name: string;
  slug: string;
  logo_url?: string;
  primary_color?: string;
  secondary_color?: string;
  species_focus?: string;
  default_phase?: Phase;
}

export interface BrandTheme {
  clientName: string;
  logoUrl: string | null;
  primaryColor: string;
  secondaryColor: string;
}

export interface GlobalContextType {
  client: ClientScope | null;
  availableClients: ClientScope[];
  branding: BrandTheme;
  species: string | null;
  phase: Phase | null;
  site: string | null;
  tank: string | null;
  setClient: Dispatch<SetStateAction<ClientScope | null>>;
  setAvailableClients: Dispatch<SetStateAction<ClientScope[]>>;
  setSpecies: Dispatch<SetStateAction<string | null>>;
  setPhase: Dispatch<SetStateAction<Phase | null>>;
  setSite: Dispatch<SetStateAction<string | null>>;
  setTank: Dispatch<SetStateAction<string | null>>;
}

const GlobalContext = createContext<GlobalContextType | undefined>(undefined);

interface GlobalProviderProps {
  children: ReactNode;
}

const DEFAULT_BRAND_THEME: BrandTheme = {
  clientName: 'AQL Vision',
  logoUrl: null,
  primaryColor: '#38bdf8',
  secondaryColor: '#0f2f5f'
};

const colorPattern = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

const normalizeColor = (value: string | undefined | null, fallback: string) => {
  if (!value) return fallback;
  const trimmed = value.trim();
  return colorPattern.test(trimmed) ? trimmed : fallback;
};

const hexToRgb = (value: string) => {
  const hex = value.replace('#', '');
  const full = hex.length === 3 ? hex.split('').map((char) => `${char}${char}`).join('') : hex;
  const parsed = Number.parseInt(full, 16);
  return `${(parsed >> 16) & 255}, ${(parsed >> 8) & 255}, ${parsed & 255}`;
};

const normalizePhaseScope = (value: unknown): Phase | null => {
  const normalized = String(value ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '-');
  if (normalized.includes('larv')) return 'larval';
  if (normalized.includes('juven')) return 'juvenile';
  if (normalized.includes('pre') || normalized.includes('grow')) return 'pre-grow';
  if (normalized.includes('adult')) return 'adult';
  return null;
};

const toBrandTheme = (client: ClientScope | null): BrandTheme => {
  if (!client) return DEFAULT_BRAND_THEME;
  return {
    clientName: client.name || DEFAULT_BRAND_THEME.clientName,
    logoUrl: client.logo_url ?? null,
    primaryColor: normalizeColor(client.primary_color, DEFAULT_BRAND_THEME.primaryColor),
    secondaryColor: normalizeColor(client.secondary_color, DEFAULT_BRAND_THEME.secondaryColor)
  };
};

export function GlobalProvider({ children }: GlobalProviderProps) {
  const [availableClients, setAvailableClients] = useState<ClientScope[]>([]);
  const [client, setClient] = useState<ClientScope | null>(null);
  const [species, setSpecies] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase | null>(null);
  const [site, setSite] = useState<string | null>(null);
  const [tank, setTank] = useState<string | null>(null);
  const previousDefaultClientIdRef = useRef<string | null>(null);

  // Load from localStorage on mount
  useEffect(() => {
    const savedClient = localStorage.getItem('aql_client');
    const savedSpecies = localStorage.getItem('aql_species');
    const savedPhase = localStorage.getItem('aql_phase');
    const savedSite = localStorage.getItem('aql_site');
    const savedTank = localStorage.getItem('aql_tank');

    if (savedClient) {
      try {
        setClient(JSON.parse(savedClient));
      } catch (e) {
        console.error('Failed to parse saved client:', e);
      }
    }
    if (savedSpecies) setSpecies(savedSpecies);
    if (savedPhase) setPhase(normalizePhaseScope(savedPhase));
    if (savedSite) setSite(savedSite);
    if (savedTank) setTank(savedTank);
  }, []);

  useEffect(() => {
    let active = true;

    const loadClients = async () => {
      if (!hasSupabaseConfig) {
        setAvailableClients([]);
        setClient(null);
        return;
      }

      const { data, error } = await supabase
        .from('aql_clients')
        .select('*')
        .eq('is_active', true)
        .order('name');

      if (!active) return;

      if (error || !data?.length) {
        setAvailableClients([]);
        setClient(null);
        return;
      }

      const clients: ClientScope[] = data.map((row: any) => ({
        id: String(row.client_id),
        name: String(row.name ?? ''),
        slug: String(row.slug ?? ''),
        logo_url: row.logo_url ? String(row.logo_url) : undefined,
        primary_color: row.primary_color ? String(row.primary_color) : undefined,
        secondary_color: row.secondary_color ? String(row.secondary_color) : undefined
      }));

      setAvailableClients(clients);
      setClient((current) => {
        if (!current) return clients[0] ?? null;
        return clients.find((item) =>
          item.id === current.id ||
          item.slug === current.slug ||
          item.name.toLowerCase() === current.name.toLowerCase()
        ) ?? clients[0] ?? null;
      });
    };

    loadClients();
    return () => {
      active = false;
    };
  }, []);

  // Save to localStorage when changed
  useEffect(() => {
    if (availableClients.length > 0) {
      localStorage.setItem('aql_available_clients', JSON.stringify(availableClients));
    } else {
      localStorage.removeItem('aql_available_clients');
    }
  }, [availableClients]);

  useEffect(() => {
    if (client) {
      localStorage.setItem('aql_client', JSON.stringify(client));
      const clientChanged = previousDefaultClientIdRef.current !== client.id;
      previousDefaultClientIdRef.current = client.id;
      if (clientChanged) {
        if (!species && client.species_focus) setSpecies(client.species_focus);
        if (!phase && client.default_phase) setPhase(normalizePhaseScope(client.default_phase));
      }
    } else {
      previousDefaultClientIdRef.current = null;
      localStorage.removeItem('aql_client');
    }
  }, [client, phase, species]);

  useEffect(() => {
    if (species) {
      localStorage.setItem('aql_species', species);
    } else {
      localStorage.removeItem('aql_species');
    }
  }, [species]);

  useEffect(() => {
    if (phase) {
      localStorage.setItem('aql_phase', phase);
    } else {
      localStorage.removeItem('aql_phase');
    }
  }, [phase]);

  useEffect(() => {
    if (site) {
      localStorage.setItem('aql_site', site);
    } else {
      localStorage.removeItem('aql_site');
    }
  }, [site]);

  useEffect(() => {
    if (tank) {
      localStorage.setItem('aql_tank', tank);
    } else {
      localStorage.removeItem('aql_tank');
    }
  }, [tank]);

  const branding = useMemo(() => toBrandTheme(client), [client]);

  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--aql-brand-primary', branding.primaryColor);
    root.style.setProperty('--aql-brand-secondary', branding.secondaryColor);
    root.style.setProperty('--aql-brand-primary-rgb', hexToRgb(branding.primaryColor));
    root.style.setProperty('--aql-brand-secondary-rgb', hexToRgb(branding.secondaryColor));
    root.style.setProperty('--primary', branding.primaryColor);
    root.style.setProperty('--ring', branding.primaryColor);
    root.style.setProperty('--sidebar-primary', branding.primaryColor);
  }, [branding]);

  const value: GlobalContextType = useMemo(() => ({
    client,
    availableClients,
    branding,
    species,
    phase,
    site,
    tank,
    setClient,
    setAvailableClients,
    setSpecies,
    setPhase,
    setSite,
    setTank,
  }), [availableClients, branding, client, phase, site, species, tank]);

  return (
    <GlobalContext.Provider value={value}>
      {children}
    </GlobalContext.Provider>
  );
}

export function useGlobal() {
  const context = useContext(GlobalContext);
  if (context === undefined) {
    throw new Error('useGlobal must be used within a GlobalProvider');
  }
  return context;
}
